import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material'
import { FormOutlined } from '@ant-design/icons'
import { Period } from 'types/periods'
import { VariableAllocationRow } from 'types/comp-allocations'
import SimpleEditorContent from './SimpleEditorContent'
import AdvancedEditorContent from './AdvancedEditorContent'
import useAuth from 'hooks/useAuth'
import { useSearchExpandedTargets } from 'hooks/useComponents'

interface EditAllocationRowDialogProps {
  open: boolean
  onClose: () => void
  onSave: (updatedRow: VariableAllocationRow) => void
  row: VariableAllocationRow | null
  selectedOrganizations: string[]
  organizationMap: Record<string, string>
  period: Period | null
}

const EditAllocationRowDialog: React.FC<EditAllocationRowDialogProps> = ({
  open,
  onClose,
  onSave,
  row,
  selectedOrganizations,
  organizationMap,
  period,
}) => {
  const { token, isLoading: isLoadingProfile } = useAuth()
  const [simpleEditor, setSimpleEditor] = useState(true)
  const [loadingTimeout, setLoadingTimeout] = useState(false)
  const [liveRow, setLiveRow] = useState<VariableAllocationRow | null>(null)

  const {
    targets,
    isLoading: isLoadingTargets,
    isError,
  } = useSearchExpandedTargets(
    token!,
    {
      organizationIds: selectedOrganizations,
      periodId: period?.id ? [period.id] : [],
      userIds: ['00000000-0000-0000-0000-000000000000'],
    },
    !!period?.id && selectedOrganizations.length > 0
  )

  // Add timeout for loading state
  useEffect(() => {
    if (isLoadingTargets || isLoadingProfile) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true)
      }, 8000) // 8 seconds timeout

      return () => clearTimeout(timeout)
    } else {
      setLoadingTimeout(false)
    }
  }, [isLoadingTargets, isLoadingProfile])

  // Check if row has multiple formula requirements and set to advanced mode
  useEffect(() => {
    if (open && row && row.formulaRequirements && row.formulaRequirements.length > 1) {
      setSimpleEditor(false)
    }
  }, [open, row])

  const resetAndClose = () => {
    setSimpleEditor(true)
    setLoadingTimeout(false)
    setLiveRow(null)
    onClose()
  }

  const handleSave = (updatedRow: VariableAllocationRow) => {
    onSave(updatedRow)
    resetAndClose()
  }

  const handleCancel = () => {
    resetAndClose()
  }

  const toggleEditorMode = () => {
    setSimpleEditor(!simpleEditor)
  }

  // Error state
  if (isError) {
    return (
      <Dialog
        open={open}
        onClose={resetAndClose}
        slotProps={{
          paper: {
            sx: {
              width: 960,
              height: 820,
              maxWidth: 'none',
              maxHeight: 'none',
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h4" color="error">
            Error Loading Dialog
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body1">Failed to load targets data. Please try again or contact support.</Typography>
          </Alert>
        </DialogContent>
      </Dialog>
    )
  }

  // Loading state
  if (!row || isLoadingProfile || isLoadingTargets) {
    return (
      <Dialog
        open={open}
        onClose={resetAndClose}
        slotProps={{
          paper: {
            sx: {
              width: 960,
              height: 820,
              maxWidth: 'none',
              maxHeight: 'none',
            },
          },
        }}
      >
        <DialogTitle>
          <Typography>Loading...</Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2 }}>
              {isLoadingProfile ? 'Loading profile...' : isLoadingTargets ? 'Loading targets...' : 'Loading...'}
            </Typography>
            {loadingTimeout && (
              <Alert severity="warning" sx={{ mt: 2, maxWidth: '400px' }}>
                Loading is taking longer than expected. Please check your connection or try refreshing.
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      slotProps={{
        paper: {
          sx: {
            width: 960,
            height: 820,
            maxWidth: 'none',
            maxHeight: 'none',
          },
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h4">
            {row.label !== ''
              ? simpleEditor
                ? `Editing Allocation Row - [${row.label}]`
                : `Advanced Editing Allocation Row [${row.label}]`
              : 'New Allocation Row'}
          </Typography>
          {simpleEditor && (row.label !== '' || (liveRow?.targetSlugs && liveRow.targetSlugs.length > 0)) && (
            <Tooltip title="Switch to Advanced Editor">
              <IconButton onClick={toggleEditorMode} color="primary">
                <FormOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {simpleEditor ? (
          <SimpleEditorContent
            row={row}
            newRow={row.label === '' ? true : false}
            period={period}
            organizationMap={organizationMap}
            expandedTargets={targets.targets || []}
            onSave={handleSave}
            onCancel={handleCancel}
            onRowChange={setLiveRow}
          />
        ) : (
          <AdvancedEditorContent
            row={liveRow || row}
            period={period}
            expandedTargets={targets.targets || []}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default EditAllocationRowDialog
